<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="modelValue" class="modal-overlay" @click.self="close">
        <div class="modal-container modal-lg">
          <div class="modal-header">
            <div class="modal-title-section">
              <div class="modal-icon">
                <vue-feather type="cpu" size="20" />
              </div>
              <h3 class="modal-title">Populate Questions</h3>
            </div>
            <button @click="close" class="modal-close" type="button">
              <vue-feather type="x" size="20" />
            </button>
          </div>
          
          <div class="modal-body">
            <form @submit.prevent="handleSubmit" class="populate-form">
              <!-- Parent Info -->
              <div v-if="parentInfo" class="parent-info-card">
                <vue-feather :type="parentIcon" size="16" />
                <span>{{ parentInfo.type }}: <strong>{{ parentInfo.name }}</strong></span>
              </div>

              <!-- Number of Questions -->
              <div class="form-section">
                <h6 class="section-title">Question Count</h6>
                <div class="form-group">
                  <label class="form-label">Total Questions <span class="text-danger">*</span></label>
                  <input 
                    v-model.number="form.totalQuestions" 
                    type="number" 
                    class="form-control" 
                    min="1"
                    max="500"
                    required
                  />
                </div>
              </div>

              <!-- Difficulty Weightage -->
              <div class="form-section">
                <h6 class="section-title">Difficulty Weightage</h6>
                <div class="weightage-toggle">
                  <label class="toggle-label">
                    <input type="checkbox" v-model="usePercentage" />
                    <span>Use Percentage</span>
                  </label>
                </div>
                
                <div class="difficulty-grid">
                  <div class="difficulty-item easy">
                    <div class="difficulty-header">
                      <span class="difficulty-badge bg-success">Easy</span>
                    </div>
                    <input 
                      v-model.number="form.easyWeight" 
                      type="number" 
                      class="form-control"
                      :min="0"
                      :max="usePercentage ? 100 : form.totalQuestions"
                    />
                    <span class="weight-suffix">{{ usePercentage ? '%' : 'questions' }}</span>
                  </div>
                  
                  <div class="difficulty-item medium">
                    <div class="difficulty-header">
                      <span class="difficulty-badge bg-warning">Medium</span>
                    </div>
                    <input 
                      v-model.number="form.mediumWeight" 
                      type="number" 
                      class="form-control"
                      :min="0"
                      :max="usePercentage ? 100 : form.totalQuestions"
                    />
                    <span class="weight-suffix">{{ usePercentage ? '%' : 'questions' }}</span>
                  </div>
                  
                  <div class="difficulty-item hard">
                    <div class="difficulty-header">
                      <span class="difficulty-badge bg-danger">Hard</span>
                    </div>
                    <input 
                      v-model.number="form.hardWeight" 
                      type="number" 
                      class="form-control"
                      :min="0"
                      :max="usePercentage ? 100 : form.totalQuestions"
                    />
                    <span class="weight-suffix">{{ usePercentage ? '%' : 'questions' }}</span>
                  </div>
                </div>
                
                <div v-if="weightageError" class="alert alert-danger mt-2">
                  {{ weightageError }}
                </div>
                <div v-else class="weightage-summary">
                  <vue-feather type="check-circle" size="14" class="text-success" />
                  <span>{{ weightageBreakdown }}</span>
                </div>
              </div>

              <!-- Test Focus Mode (for Test Series) -->
              <div v-if="showTopicWeightage && subjectsWithQuestions.length" class="form-section">
                <h6 class="section-title">Test Focus</h6>
                <div class="focus-options">
                  <label class="focus-option" :class="{ active: testFocusMode === 'full' }">
                    <input type="radio" v-model="testFocusMode" value="full" />
                    <vue-feather type="layers" size="14" />
                    <span>Full Test</span>
                  </label>
                  <label class="focus-option" :class="{ active: testFocusMode === 'subject' }">
                    <input type="radio" v-model="testFocusMode" value="subject" />
                    <vue-feather type="book" size="14" />
                    <span>Subject Wise</span>
                  </label>
                  <label class="focus-option" :class="{ active: testFocusMode === 'topic' }">
                    <input type="radio" v-model="testFocusMode" value="topic" />
                    <vue-feather type="bookmark" size="14" />
                    <span>Topic Wise</span>
                  </label>
                </div>

                <!-- Subject picker for subject-wise or topic-wise -->
                <div v-if="testFocusMode === 'subject' || testFocusMode === 'topic'" class="focus-picker mt-3">
                  <div class="form-group">
                    <label class="form-label">Select Subject <span class="text-danger">*</span></label>
                    <select v-model="focusSubjectId" class="form-control">
                      <option value="">-- Choose Subject --</option>
                      <option v-for="s in subjects" :key="s.id" :value="s.id">{{ s.name }}</option>
                    </select>
                  </div>
                </div>

                <!-- Topic picker for topic-wise -->
                <div v-if="testFocusMode === 'topic' && focusSubjectId" class="focus-picker mt-2">
                  <div class="form-group">
                    <label class="form-label">Select Topic <span class="text-danger">*</span></label>
                    <select v-model="focusTopicId" class="form-control">
                      <option value="">-- Choose Topic --</option>
                      <option v-for="t in focusSubjectTopics" :key="t.id" :value="t.id">{{ t.name }}</option>
                    </select>
                  </div>
                </div>

                <div v-if="testFocusMode !== 'full'" class="focus-hint mt-2">
                  <vue-feather type="info" size="13" class="text-muted" />
                  <span class="text-muted" style="font-size: 0.8rem;">
                    {{ testFocusMode === 'subject' 
                      ? 'All questions will be drawn from the selected subject. Topics under it will be weighted equally.' 
                      : 'All questions will be drawn from the selected topic (100% weightage).' }}
                  </span>
                </div>
              </div>

              <!-- Questions per Subject (for Test Series) -->
              <div v-if="showTopicWeightage && subjectsWithQuestions.length" class="form-section">
                <h6 class="section-title">
                  Questions per Subject
                  <button type="button" class="btn btn-sm btn-outline-secondary ms-2" @click="resetSubjectQuestions">
                    Reset to Default
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-danger ms-1" @click="zeroOutSubjectQuestions">
                    Reset to Zero
                  </button>
                </h6>
                <div class="subject-questions-list">
                  <div v-for="subject in subjectsWithQuestions" :key="subject.id" class="subject-question-item">
                    <div class="subject-info">
                      <span class="subject-name">{{ subject.name }}</span>
                    </div>
                    <div class="subject-question-input">
                      <input 
                        v-model.number="subject.questionCount" 
                        type="number" 
                        class="form-control form-control-sm"
                        min="0"
                        :max="form.totalQuestions"
                      />
                      <span class="weight-suffix">questions</span>
                    </div>
                  </div>
                </div>
                <div v-if="subjectQuestionsError" class="alert alert-danger mt-2">
                  {{ subjectQuestionsError }}
                </div>
                <div v-else class="weightage-summary">
                  <vue-feather type="check-circle" size="14" class="text-success" />
                  <span>Total: {{ totalSubjectQuestions }} questions</span>
                </div>
              </div>

              <!-- Topic Weightage (for Test Series) -->
              <div v-if="showTopicWeightage && topics.length" class="form-section">
                <h6 class="section-title">
                  Topic Weightage
                  <button type="button" class="btn btn-sm btn-outline-secondary ms-2" @click="resetTopicWeights">
                    Reset to Default
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-danger ms-1" @click="zeroOutTopicWeights">
                    Reset to Zero
                  </button>
                </h6>
                <div class="topic-weightage-list">
                  <div v-for="topic in topicsWithWeights" :key="topic.id" class="topic-weight-item">
                    <div class="topic-info">
                      <span class="topic-name">{{ topic.name }}</span>
                      <span class="topic-questions text-muted">({{ topic.questionCount || 0 }} questions available)</span>
                    </div>
                    <div class="topic-weight-input">
                      <input 
                        v-model.number="topic.weight" 
                        type="number" 
                        class="form-control form-control-sm"
                        min="0"
                        max="100"
                      />
                      <span class="weight-suffix">%</span>
                    </div>
                  </div>
                </div>
                <div v-if="topicWeightageError" class="alert alert-danger mt-2">
                  {{ topicWeightageError }}
                </div>
                <div v-else class="weightage-summary">
                  <vue-feather type="check-circle" size="14" class="text-success" />
                  <span>Total: {{ totalTopicWeight }}%</span>
                </div>
              </div>

              <!-- Scoring Pattern -->
              <div class="form-section">
                <h6 class="section-title">Scoring Pattern</h6>
                <div class="form-row">
                  <div class="form-group">
                    <label class="form-label">Marks for Correct Answer <span class="text-danger">*</span></label>
                    <input 
                      v-model.number="form.correctMarks" 
                      type="number" 
                      class="form-control"
                      min="0.25"
                      step="0.25"
                      required
                    />
                  </div>
                  <div class="form-group">
                    <label class="form-label">Marks for Wrong Answer (Negative)</label>
                    <input 
                      v-model.number="form.negativeMarks" 
                      type="number" 
                      class="form-control"
                      min="0"
                      step="0.25"
                    />
                    <small class="text-muted">Leave 0 for no negative marking</small>
                  </div>
                </div>
              </div>

              <!-- Question Type -->
              <div class="form-section">
                <h6 class="section-title">Question Type</h6>
                <div class="form-group">
                  <select v-model="form.questionType" class="form-control">
                    <option value="mcq">Multiple Choice (MCQ)</option>
                    <option value="numerical">Numerical</option>
                    <option value="mixed">Mixed (MCQ + Numerical)</option>
                  </select>
                </div>
              </div>
            </form>
          </div>
          
          <div class="modal-footer">
            <div class="footer-buttons">
              <button type="button" class="btn btn-outline-secondary" @click="close" :disabled="loading">
                Cancel
              </button>
              <button 
                type="button" 
                class="btn btn-primary" 
                @click="handleSubmit" 
                :disabled="loading || !isValid"
              >
                <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
                <vue-feather v-else type="cpu" size="14" class="me-1" />
                Generate Questions
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import api from '@/services/api'

export default {
  name: 'PopulateQuestionsModal',
  props: {
    modelValue: { type: Boolean, default: false },
    testId: { type: String, required: true },
    parentType: { type: String, default: null }, // 'series', 'topic', 'lesson'
    parentId: { type: String, default: null },
    parentName: { type: String, default: '' }
  },
  emits: ['update:modelValue', 'success'],
  setup(props, { emit }) {
    const loading = ref(false)
    const usePercentage = ref(true)
    const topics = ref([])
    const topicsWithWeights = ref([])
    const subjects = ref([])
    const subjectsWithQuestions = ref([])
    const testFocusMode = ref('full') // 'full', 'subject', 'topic'
    const focusSubjectId = ref('')
    const focusTopicId = ref('')
    
    const form = reactive({
      totalQuestions: 20,
      easyWeight: 30,
      mediumWeight: 50,
      hardWeight: 20,
      correctMarks: 1,
      negativeMarks: 0.25,
      questionType: 'mcq'
    })

    const showTopicWeightage = computed(() => props.parentType === 'series')

    // Topics belonging to the currently focused subject (for topic-wise picker)
    const focusSubjectTopics = computed(() => {
      if (!focusSubjectId.value) return []
      return topics.value.filter(t => t.subjectId === focusSubjectId.value)
    })
    
    const parentIcon = computed(() => {
      const icons = { series: 'layers', topic: 'book', lesson: 'file-text' }
      return icons[props.parentType] || 'folder'
    })

    const parentInfo = computed(() => {
      if (!props.parentType) return null
      const types = { series: 'Test Series', topic: 'Topic', lesson: 'Lesson' }
      return { type: types[props.parentType], name: props.parentName }
    })

    const weightageError = computed(() => {
      const total = form.easyWeight + form.mediumWeight + form.hardWeight
      if (usePercentage.value) {
        if (total !== 100) return `Difficulty weightage must total 100% (currently ${total}%)`
      } else {
        if (total !== form.totalQuestions) {
          return `Question count must equal ${form.totalQuestions} (currently ${total})`
        }
      }
      return null
    })

    const weightageBreakdown = computed(() => {
      if (usePercentage.value) {
        const easy = Math.round(form.totalQuestions * form.easyWeight / 100)
        const medium = Math.round(form.totalQuestions * form.mediumWeight / 100)
        const hard = form.totalQuestions - easy - medium
        return `Easy: ${easy}, Medium: ${medium}, Hard: ${hard} questions`
      }
      return `Easy: ${form.easyWeight}, Medium: ${form.mediumWeight}, Hard: ${form.hardWeight} questions`
    })

    const totalTopicWeight = computed(() => {
      return topicsWithWeights.value.reduce((sum, t) => sum + (t.weight || 0), 0)
    })

    const topicWeightageError = computed(() => {
      if (!showTopicWeightage.value || !topics.value.length) return null
      if (totalTopicWeight.value !== 100) {
        return `Topic weightage must total 100% (currently ${totalTopicWeight.value}%)`
      }
      return null
    })

    const totalSubjectQuestions = computed(() => {
      return subjectsWithQuestions.value.reduce((sum, s) => sum + (s.questionCount || 0), 0)
    })

    const subjectQuestionsError = computed(() => {
      if (!showTopicWeightage.value || !subjects.value.length) return null
      if (totalSubjectQuestions.value !== form.totalQuestions) {
        return `Subject questions must total ${form.totalQuestions} (currently ${totalSubjectQuestions.value})`
      }
      return null
    })

    const isValid = computed(() => {
      if (!form.totalQuestions || form.totalQuestions < 1) return false
      if (weightageError.value) return false
      if (showTopicWeightage.value && topicWeightageError.value) return false
      if (showTopicWeightage.value && subjectQuestionsError.value) return false
      if (!form.correctMarks || form.correctMarks <= 0) return false
      return true
    })

    const fetchTopics = async () => {
      if (!showTopicWeightage.value || !props.parentId) return
      
      try {
        // Fetch topics for the test series (via exam)
        const seriesRes = await api.get(`/v1/admin/testseries/${props.parentId}`)
        const examId = seriesRes.data.testSeries?.examId
        
        if (examId) {
          // Get subjects for the exam
          const subjectsRes = await api.get(`/v1/admin/exams/${examId}/subjects`)
          const fetchedSubjects = subjectsRes.data.subjects || []
          subjects.value = fetchedSubjects
          resetSubjectQuestions()
          
          // Get topics for each subject
          const allTopics = []
          for (const subject of fetchedSubjects) {
            const topicsRes = await api.get(`/v1/admin/subjects/${subject.id}/topics`)
            const subjectTopics = topicsRes.data.topics || []
            allTopics.push(...subjectTopics.map(t => ({
              ...t,
              subjectName: subject.name
            })))
          }
          
          topics.value = allTopics
          resetTopicWeights()
        }
      } catch (err) {
        console.error('Failed to fetch topics:', err)
      }
    }

    const resetSubjectQuestions = () => {
      const subjectCount = subjects.value.length
      if (!subjectCount) {
        subjectsWithQuestions.value = []
        return
      }
      
      const baseCount = Math.floor(form.totalQuestions / subjectCount)
      const remainder = form.totalQuestions % subjectCount
      
      // Distribute questions equally, spreading remainder across subjects
      subjectsWithQuestions.value = subjects.value.map((subject, index) => ({
        ...subject,
        questionCount: baseCount + (index < remainder ? 1 : 0)
      }))
    }

    const resetTopicWeights = () => {
      const topicCount = topics.value.length
      if (!topicCount) {
        topicsWithWeights.value = []
        return
      }
      
      // Get subject IDs with 0 questions
      const excludedSubjectIds = new Set(
        subjectsWithQuestions.value
          .filter(s => !s.questionCount || s.questionCount === 0)
          .map(s => s.id)
      )
      
      // Filter topics to only include those from subjects with questions > 0
      const activeTopics = topics.value.filter(t => !excludedSubjectIds.has(t.subjectId))
      const activeTopicCount = activeTopics.length
      
      // Check if any topic has a weightage from server
      const hasServerWeightage = topics.value.some(t => t.weightage !== undefined && t.weightage !== null)
      
      if (hasServerWeightage && activeTopicCount === topicCount) {
        // Use server-provided weightage only if no subjects are excluded
        topicsWithWeights.value = topics.value.map(topic => ({
          ...topic,
          weight: topic.weightage || 0
        }))
      } else {
        // Split equally among active topics, set 0 for excluded topics
        const baseWeight = activeTopicCount > 0 ? Math.floor(100 / activeTopicCount) : 0
        const remainder = activeTopicCount > 0 ? 100 % activeTopicCount : 0
        
        let activeIndex = 0
        topicsWithWeights.value = topics.value.map(topic => {
          if (excludedSubjectIds.has(topic.subjectId)) {
            return { ...topic, weight: 0 }
          }
          const weight = baseWeight + (activeIndex < remainder ? 1 : 0)
          activeIndex++
          return { ...topic, weight }
        })
      }
    }

    const applyTestFocus = () => {
      if (testFocusMode.value === 'full') {
        // Revert to default distribution
        resetSubjectQuestions()
        resetTopicWeights()
        return
      }

      if (testFocusMode.value === 'subject' && focusSubjectId.value) {
        // All questions go to the selected subject, 0 for others
        subjectsWithQuestions.value = subjects.value.map(s => ({
          ...s,
          questionCount: s.id === focusSubjectId.value ? form.totalQuestions : 0
        }))
        // Topics under that subject get equal weight, others get 0
        const subjectTopics = topics.value.filter(t => t.subjectId === focusSubjectId.value)
        const count = subjectTopics.length
        const baseWeight = count > 0 ? Math.floor(100 / count) : 0
        const remainder = count > 0 ? 100 % count : 0
        let idx = 0
        topicsWithWeights.value = topics.value.map(topic => {
          if (topic.subjectId !== focusSubjectId.value) {
            return { ...topic, weight: 0 }
          }
          const w = baseWeight + (idx < remainder ? 1 : 0)
          idx++
          return { ...topic, weight: w }
        })
        return
      }

      if (testFocusMode.value === 'topic' && focusSubjectId.value && focusTopicId.value) {
        // All questions go to the parent subject, 0 for others
        subjectsWithQuestions.value = subjects.value.map(s => ({
          ...s,
          questionCount: s.id === focusSubjectId.value ? form.totalQuestions : 0
        }))
        // 100% weight to the selected topic, 0 for everything else
        topicsWithWeights.value = topics.value.map(topic => ({
          ...topic,
          weight: topic.id === focusTopicId.value ? 100 : 0
        }))
      }
    }

    const zeroOutSubjectQuestions = () => {
      subjectsWithQuestions.value = subjects.value.map(subject => ({
        ...subject,
        questionCount: 0
      }))
    }

    const zeroOutTopicWeights = () => {
      topicsWithWeights.value = topics.value.map(topic => ({
        ...topic,
        weight: 0
      }))
    }

    const close = () => {
      emit('update:modelValue', false)
    }

    const handleSubmit = async () => {
      if (!isValid.value) return
      
      loading.value = true
      try {
        // Calculate actual question counts
        let easyCount, mediumCount, hardCount
        if (usePercentage.value) {
          easyCount = Math.round(form.totalQuestions * form.easyWeight / 100)
          mediumCount = Math.round(form.totalQuestions * form.mediumWeight / 100)
          hardCount = form.totalQuestions - easyCount - mediumCount
        } else {
          easyCount = form.easyWeight
          mediumCount = form.mediumWeight
          hardCount = form.hardWeight
        }

        const payload = {
          testId: props.testId,
          totalQuestions: form.totalQuestions,
          difficultyWeightage: {
            easy: easyCount,
            medium: mediumCount,
            hard: hardCount
          },
          scoringPattern: {
            correctMarks: form.correctMarks,
            negativeMarks: form.negativeMarks
          },
          questionType: form.questionType
        }

        // Add topic weightage for test series
        if (showTopicWeightage.value && topicsWithWeights.value.length) {
          payload.topicWeightage = topicsWithWeights.value
            .filter(t => t.weight > 0)
            .map(t => ({
              topicId: t.id,
              weight: t.weight
            }))
        }

        // Add subject questions for test series
        if (showTopicWeightage.value && subjectsWithQuestions.value.length) {
          payload.subjectQuestions = subjectsWithQuestions.value
            .filter(s => s.questionCount > 0)
            .map(s => ({
              subjectId: s.id,
              questionCount: s.questionCount
            }))
        }

        await api.post(`/v1/admin/tests/${props.testId}/populate-questions`, payload)
        
        emit('success')
        close()
      } catch (err) {
        console.error('Failed to populate questions:', err)
      } finally {
        loading.value = false
      }
    }

    watch(() => props.modelValue, (newVal) => {
      if (newVal && showTopicWeightage.value) {
        testFocusMode.value = 'full'
        focusSubjectId.value = ''
        focusTopicId.value = ''
        fetchTopics()
      }
    })

    // Reset subject questions when total questions changes
    watch(() => form.totalQuestions, () => {
      if (subjects.value.length) {
        if (testFocusMode.value === 'full') {
          resetSubjectQuestions()
        } else {
          applyTestFocus()
        }
      }
    })

    // Recalculate topic weights when subject question counts change
    watch(
      () => subjectsWithQuestions.value.map(s => s.questionCount),
      () => {
        // Only auto-recalculate in full mode; focus modes handle their own mapping
        if (topics.value.length && testFocusMode.value === 'full') {
          resetTopicWeights()
        }
      },
      { deep: true }
    )

    // Watch test focus mode changes
    watch(testFocusMode, (newMode) => {
      if (newMode === 'full') {
        focusSubjectId.value = ''
        focusTopicId.value = ''
      }
      focusTopicId.value = ''
      applyTestFocus()
    })

    // When focus subject changes
    watch(focusSubjectId, () => {
      focusTopicId.value = '' // reset topic when subject changes
      applyTestFocus()
    })

    // When focus topic changes
    watch(focusTopicId, () => {
      applyTestFocus()
    })

    onMounted(() => {
      if (props.modelValue && showTopicWeightage.value) {
        fetchTopics()
      }
    })

    return {
      loading,
      usePercentage,
      form,
      topics,
      topicsWithWeights,
      subjects,
      subjectsWithQuestions,
      showTopicWeightage,
      testFocusMode,
      focusSubjectId,
      focusTopicId,
      focusSubjectTopics,
      parentIcon,
      parentInfo,
      weightageError,
      weightageBreakdown,
      totalTopicWeight,
      topicWeightageError,
      totalSubjectQuestions,
      subjectQuestionsError,
      isValid,
      resetTopicWeights,
      resetSubjectQuestions,
      zeroOutSubjectQuestions,
      zeroOutTopicWeights,
      close,
      handleSubmit
    }
  }
}
</script>


<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-container {
  background: white;
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-lg {
  max-width: 700px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.modal-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.modal-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.modal-close {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: #f3f4f6;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #e5e7eb;
  color: #374151;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
}

.footer-buttons {
  display: flex;
  gap: 0.75rem;
}

.populate-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.parent-info-card {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  color: #0369a1;
  font-size: 0.875rem;
}

.form-section {
  background: #f9fafb;
  padding: 1rem;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.section-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 500;
  font-size: 0.8125rem;
  color: #4b5563;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-control {
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.weightage-toggle {
  margin-bottom: 1rem;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #4b5563;
}

.difficulty-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.difficulty-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.difficulty-header {
  display: flex;
  justify-content: center;
}

.difficulty-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
}

.weight-suffix {
  font-size: 0.75rem;
  color: #6b7280;
  text-align: center;
}

.weightage-summary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  font-size: 0.8125rem;
  color: #059669;
}

.topic-weightage-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 200px;
  overflow-y: auto;
}

.subject-questions-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 200px;
  overflow-y: auto;
}

.subject-question-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.subject-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.subject-name {
  font-weight: 500;
  font-size: 0.875rem;
  color: #374151;
}

.subject-question-input {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.subject-question-input .form-control {
  width: 70px;
  text-align: center;
}

.topic-weight-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.topic-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.topic-name {
  font-weight: 500;
  font-size: 0.875rem;
  color: #374151;
}

.topic-questions {
  font-size: 0.75rem;
}

.topic-weight-input {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.topic-weight-input .form-control {
  width: 70px;
  text-align: center;
}

.focus-options {
  display: flex;
  gap: 0.75rem;
}

.focus-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #4b5563;
  background: white;
  transition: all 0.2s;
  flex: 1;
  justify-content: center;
}

.focus-option input[type="radio"] {
  display: none;
}

.focus-option:hover {
  border-color: #6366f1;
  color: #6366f1;
}

.focus-option.active {
  border-color: #6366f1;
  background: #eef2ff;
  color: #4f46e5;
}

.focus-picker .form-control {
  margin-top: 0.25rem;
}

.focus-hint {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
}

.alert {
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.8125rem;
}

.alert-danger {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
}

/* Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.95) translateY(-10px);
}

/* Dark mode */
:global(body.dark-mode) .modal-container {
  background: #1e293b;
}

:global(body.dark-mode) .modal-header {
  border-color: #334155;
}

:global(body.dark-mode) .modal-title {
  color: #f1f5f9;
}

:global(body.dark-mode) .modal-close {
  background: #334155;
  color: #94a3b8;
}

:global(body.dark-mode) .modal-close:hover {
  background: #475569;
  color: #e2e8f0;
}

:global(body.dark-mode) .modal-footer {
  border-color: #334155;
}

:global(body.dark-mode) .form-section {
  background: #0f172a;
  border-color: #334155;
}

:global(body.dark-mode) .section-title {
  color: #e2e8f0;
}

:global(body.dark-mode) .form-label {
  color: #cbd5e1;
}

:global(body.dark-mode) .form-control {
  background: #1e293b;
  border-color: #475569;
  color: #f1f5f9;
}

:global(body.dark-mode) .difficulty-item,
:global(body.dark-mode) .topic-weight-item,
:global(body.dark-mode) .subject-question-item {
  background: #1e293b;
  border-color: #334155;
}

:global(body.dark-mode) .topic-name,
:global(body.dark-mode) .subject-name {
  color: #e2e8f0;
}

:global(body.dark-mode) .parent-info-card {
  background: #1e3a5f;
  border-color: #2563eb;
  color: #93c5fd;
}

:global(body.dark-mode) .focus-option {
  background: #1e293b;
  border-color: #475569;
  color: #cbd5e1;
}

:global(body.dark-mode) .focus-option:hover {
  border-color: #818cf8;
  color: #a5b4fc;
}

:global(body.dark-mode) .focus-option.active {
  border-color: #818cf8;
  background: #312e81;
  color: #c7d2fe;
}
</style>
